import type { TextInputSuffix } from '@ksyos/design-system';
import type { PaginationEventHandlersParams } from '@ksyos/design-system/dist/components/pagination';
import {
    ActionBar,
    Badge,
    Box,
    Button,
    ButtonRow,
    Checkbox,
    CheckboxGroup,
    DropdownFilter,
    Heading,
    Link,
    PageHeader,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableCellSelectRow,
    TableContainer,
    TableHead,
    TableHeader,
    TableHeaderSelectAll,
    TableRow,
    Text,
    TextInput,
    Tooltip,
} from '@ksyos/design-system';
import { Link as ReactRouterLink, useNavigate, useSearchParams } from '@ksyos/router';
import { useCallback, useEffect, useState } from 'react';
import type { IServiceGroup } from '@common/interfaces';
import { ManagerAuthorization, OrganizationKgoStatus } from '@common/enums';
import { KgbExportType } from '@common/enums/kgbExportType';
import { OrganizationStructureType } from '@common/enums/organizationStructureType';
import { findActiveAgbCode } from '@common/interfaces/agbCode';
import { tryParseInt } from '@common/utils/tryParseInt';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { assertHasAuthorization, hasOneAuthorization, useAbortableFetch } from '@frontend/utils';
import type { IEpisodeCategory } from '../../../../common/interfaces/episodeCategory';
import type { IServicesByServiceGroupForEpisodeCategory } from '../../../../common/interfaces/servicesByServiceGroupForEpisodeCategory';
import type { IOrganizationFilters } from '../../../../common/validation/organization';
import type { IGroup } from '../../interfaces/group';
import type { IOrganization } from '../../interfaces/organization';
import { ActiveOrInactive } from '../../../../common/enums/activeOrInactive';
import { OrganizationType } from '../../../../common/enums/organizationType';
import { State } from '../../../../common/enums/state';
import { never } from '../../../../common/utils/never';
import { getSelectedLanguage } from '../../../translate';
import { BadgeWithLabel } from '../../elements/BadgeWithLabel';
import { TextWithMatchingTermInBold } from '../../elements/common';
import { Figure } from '../../elements/figure/Figure';
import { KgbExportModal } from '../../elements/KgbExportModal';
import { OrganizationListDrawer } from './OrganizationListDrawer';

enum OrganizationDropdownFilter {
    careGroup = 'careGroup',
    carePath = 'carePath',
    kgoStatus = 'kgoStatus',
    organizationType = 'organizationType',
    state = 'state',
    status = 'status',
}

//TODO: CS-25631 the icons needs to be added to the Design System
// as the DS Icon component doesnt support custom paths.
// for now i have added them here.
function organizationStructureTypeIcon(organizationStructureType: OrganizationStructureType) {
    switch (organizationStructureType) {
        case OrganizationStructureType.branch:
            return (
                <svg
                    fill="none"
                    height="18"
                    viewBox="0 0 19 18"
                    width="19"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <g clipPath="url(#clip0_177_12775)">
                        <path
                            d="M10.4951 1.52797L17.8181 8.26397C18.0133 8.44352 18.1292 8.69326 18.1403 8.95825C18.1513 9.22324 18.0567 9.48177 17.8771 9.67697C17.6976 9.87217 17.4478 9.98805 17.1828 9.99911C16.9179 10.0102 16.6593 9.91552 16.4641 9.73597L16.1411 9.43897V15.007C16.1401 15.5364 15.9288 16.0438 15.5537 16.4175C15.1787 16.7913 14.6706 17.0008 14.1411 17H4.14113C3.03813 17 2.14113 16.11 2.14113 15.007V9.43897L1.81813 9.73597C1.72147 9.82488 1.60826 9.89388 1.48494 9.93902C1.36162 9.98417 1.23062 10.0046 1.09941 9.99911C0.834418 9.98805 0.584677 9.87217 0.405126 9.67697C0.316221 9.58032 0.247224 9.4671 0.202074 9.34378C0.156924 9.22047 0.136505 9.08946 0.141984 8.95825C0.153049 8.69326 0.268927 8.44352 0.464126 8.26397L7.78713 1.52797C8.15628 1.1884 8.63955 0.999939 9.14113 0.999939C9.6427 0.999939 10.126 1.1884 10.4951 1.52797ZM9.14113 2.99997L4.14113 7.59997V15H8.14113V12C8.14113 11.7348 8.24648 11.4804 8.43402 11.2929C8.62156 11.1053 8.87591 11 9.14113 11C9.40634 11 9.6607 11.1053 9.84823 11.2929C10.0358 11.4804 10.1411 11.7348 10.1411 12V15H14.1411V7.59997L9.14113 2.99997Z"
                            fill="#8C7E75"
                        />
                    </g>
                    <defs>
                        <clipPath id="clip0_177_12775">
                            <rect
                                fill="white"
                                height="18"
                                transform="translate(0.141113)"
                                width="18"
                            />
                        </clipPath>
                    </defs>
                </svg>
            );
        case OrganizationStructureType.organization:
            return (
                <svg
                    fill="none"
                    height="19"
                    viewBox="0 0 19 19"
                    width="19"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <g clipPath="url(#clip0_177_12789)">
                        <path
                            d="M7.49951 16.7007H10.4995V2.70068H2.49951V16.7007H5.49951V13.7007C5.49951 13.4355 5.60487 13.1811 5.79241 12.9936C5.97994 12.806 6.2343 12.7007 6.49951 12.7007C6.76473 12.7007 7.01908 12.806 7.20662 12.9936C7.39415 13.1811 7.49951 13.4355 7.49951 13.7007V16.7007ZM12.4995 4.70068H15.4995C16.0299 4.70068 16.5387 4.9114 16.9137 5.28647C17.2888 5.66154 17.4995 6.17025 17.4995 6.70068V16.7007C17.4995 17.2311 17.2888 17.7398 16.9137 18.1149C16.5387 18.49 16.0299 18.7007 15.4995 18.7007H2.49951C1.96908 18.7007 1.46037 18.49 1.0853 18.1149C0.710225 17.7398 0.499512 17.2311 0.499512 16.7007V2.70068C0.499512 2.17025 0.710225 1.66154 1.0853 1.28647C1.46037 0.911397 1.96908 0.700684 2.49951 0.700684L10.4995 0.700684C11.0299 0.700684 11.5387 0.911397 11.9137 1.28647C12.2888 1.66154 12.4995 2.17025 12.4995 2.70068V4.70068ZM12.4995 16.7007H15.4995V6.70068H12.4995V16.7007ZM6.49951 10.7007C6.2343 10.7007 5.97994 10.5953 5.79241 10.4078C5.60487 10.2203 5.49951 9.9659 5.49951 9.70068C5.49951 9.43547 5.60487 9.18111 5.79241 8.99358C5.97994 8.80604 6.2343 8.70068 6.49951 8.70068C6.76473 8.70068 7.01908 8.80604 7.20662 8.99358C7.39415 9.18111 7.49951 9.43547 7.49951 9.70068C7.49951 9.9659 7.39415 10.2203 7.20662 10.4078C7.01908 10.5953 6.76473 10.7007 6.49951 10.7007ZM6.49951 6.70068C6.2343 6.70068 5.97994 6.59533 5.79241 6.40779C5.60487 6.22025 5.49951 5.9659 5.49951 5.70068C5.49951 5.43547 5.60487 5.18111 5.79241 4.99358C5.97994 4.80604 6.2343 4.70068 6.49951 4.70068C6.76473 4.70068 7.01908 4.80604 7.20662 4.99358C7.39415 5.18111 7.49951 5.43547 7.49951 5.70068C7.49951 5.9659 7.39415 6.22025 7.20662 6.40779C7.01908 6.59533 6.76473 6.70068 6.49951 6.70068Z"
                            fill="#8C7E75"
                        />
                    </g>
                    <defs>
                        <clipPath id="clip0_177_12789">
                            <rect
                                fill="white"
                                height="18"
                                transform="translate(0.499512 0.700684)"
                                width="18"
                            />
                        </clipPath>
                    </defs>
                </svg>
            );
    }
}

export function OrganizationsList() {
    const abortableFetch = useAbortableFetch();

    const translate = getSelectedLanguage();

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const pageSizes = [10, 15, 20, 25, 50, 100];

    const initialParsedUrl = parseUrl();
    const [page, setPage] = useState(initialParsedUrl.page);
    const [pageSize, setPageSize] = useState(initialParsedUrl.pageSize);

    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
    const [totalItems, setTotalItems] = useState(0);
    const [filters, setFilters] = useState<IOrganizationFilters>({
        isActive: ActiveOrInactive.active,
    });
    const [sortKey, setSortKey] = useState<'name' | 'city'>('name');

    // data
    const [items, setItems] = useState<IOrganization[]>([]);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [groupsListSearchText, setGroupsListSearchText] = useState<string | null>(null);
    const [groupsList, setGroupsList] = useState<IGroup[]>([]);
    const [groupsFilterListSize, setGroupsFilterListSize] = useState(10);
    const [serviceGroupMembershipsGroupedByEpisodeCategories, setServiceGroupMembershipsGroupedByEpisodeCategories] =
        useState<IServicesByServiceGroupForEpisodeCategory[]>([]);
    const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
    const [areAllChecked, setAreAllChecked] = useState(false);
    const [statuses, setStatuses] = useState<ActiveOrInactive[]>([ActiveOrInactive.active]);
    const [kgoStatuses, setKgoStatuses] = useState<OrganizationKgoStatus[]>([]);

    // ui reset filters
    const [onTextBoxFilterReset, setOnTextBoxFilterReset] = useState(false);
    const [onDropdownFilterReset, setOnDropdownFilterReset] = useState(false);
    const [onGroupsListFilterReset, setOnGroupsListFilterReset] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showSelectedOrganizationDrawer, setShowSelectedOrganizationDrawer] = useState(false);

    // misc
    const [shouldRedirect, setShouldRedirect] = useState(false);
    const [redirectTo, setRedirectTo] = useState<string>();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        assertHasAuthorization(ManagerAuthorization.organizationRead);
        loadGroupsList();
        loadAvailableCarePathsWithServiceGroups();
    }, []);

    useEffect(() => {
        validatePageSearchParam();
    }, [totalItems]);

    useEffect(() => {
        const textFilters = [filters.nameOrId, filters.agbCode, filters.cityOrZip, filters.relationNumber];
        const searchBoxesAreNotEmpty = textFilters.some(
            (textFilter) => textFilter !== null && textFilter !== undefined
        );
        if (onTextBoxFilterReset && !searchBoxesAreNotEmpty) {
            setPage(1);
            setOnTextBoxFilterReset(false);
        }
    }, [onTextBoxFilterReset]);

    useEffect(() => {
        const urlParams = parseUrl();
        setPage(urlParams.page);
        setPageSize(urlParams.pageSize);
    }, [searchParams]);

    useEffect(() => {
        navigate(`/organizations?page=${page}&limit=${pageSize}`);
        loadAllData();
    }, [page, pageSize, sortKey, sortDirection, onDropdownFilterReset, groupsFilterListSize]);

    useEffect(() => {
        if (onDropdownFilterReset) {
            setOnDropdownFilterReset(false);
            setPage(1);
        }
    }, [onDropdownFilterReset]);

    useEffect(() => {
        if (onGroupsListFilterReset) {
            loadGroupsList();
            setOnGroupsListFilterReset(false);
        }
    }, [onGroupsListFilterReset]);

    function validatePageSearchParam() {
        if (totalItems === 0) {
            return;
        }
        const totalPages = Math.max(Math.ceil(totalItems / pageSize), 1);
        const param = tryParseInt(searchParams.getOptional('page'));
        if (param && param > totalPages) {
            setPage(1);
        }
    }

    useEffect(() => {
        if (statuses.length === 2) {
            // show both active and inactive
            removeFilter('isActive');
        } else {
            handleChangeFilter('isActive', statuses[0] as ActiveOrInactive);
        }

        setOnDropdownFilterReset(true);
    }, [statuses]);

    useEffect(() => {
        if (kgoStatuses.length === 0) {
            removeFilter('kgoStatuses');
        } else {
            handleChangeFilter('kgoStatuses', kgoStatuses);
        }
    }, [kgoStatuses]);

    const handlePaginationChange = useCallback(
        ({ page: newPage, pageSize: newPageSize }: PaginationEventHandlersParams) => {
            setPageSize(newPageSize);
            setPage(newPage);
        },
        [setPageSize, setPage]
    );

    const suffixProp: TextInputSuffix = {
        icon: 'search',
        labelText: 'aria labelled text',
        onSuffixClick: () => {
            void loadAllData();
        },
    };

    async function loadAllData() {
        await Promise.all([loadData(), loadGroupsList()]);
    }

    async function loadData() {
        const url = `/api/organizations/list?page=${page}&limit=${pageSize}`;

        try {
            setIsLoading(true);
            const result = await abortableFetch.fetchJson(url, 'POST', {
                filters: filters,
                sortBy: sortKey,
                sortDirection: sortDirection,
            });
            setItems(result.json.items);
            setTotalItems(result.json.total);
        } finally {
            setIsLoading(false);
        }
    }

    function parseUrl() {
        const parsedPage = tryParseInt(searchParams.getOptional('page'), 1);
        const parsedLimit = tryParseInt(searchParams.getOptional('limit'), 15);
        const validatedLimit = pageSizes.includes(parsedLimit) ? parsedLimit : 15;

        return { page: parsedPage, pageSize: validatedLimit };
    }

    function isRowSelected(selectedId: string) {
        return !!selectedItems.includes(selectedId);
    }

    async function handleSelectRow(id: string) {
        let newSelectedItems = [...selectedItems];

        if (areAllChecked) {
            newSelectedItems = items
                .map((organization) => organization.id)
                .filter((organizationId) => organizationId !== id);
            setAreAllChecked(false);
            setSelectedItems(newSelectedItems);
        } else if (isRowSelected(id)) {
            const index = newSelectedItems.indexOf(id);
            newSelectedItems.splice(index, 1);
        } else {
            newSelectedItems.push(id);
        }

        setSelectedItems(newSelectedItems);
    }

    function isGroupSelected(groupId: string) {
        return !!filters.groupIds?.includes(groupId);
    }

    async function handleSelectGroup(groupId: string) {
        const newGroupIds = filters.groupIds ?? [];
        if (isGroupSelected(groupId)) {
            newGroupIds.splice(newGroupIds.indexOf(groupId), 1);
        } else {
            newGroupIds.push(groupId);
        }
        await handleChangeFilter('groupIds', newGroupIds);
    }

    function isOrganizationTypeSelected(organizationType: OrganizationType) {
        return !!filters.organizationTypes?.includes(organizationType);
    }

    async function handleSelectType(organizationType: OrganizationType) {
        const newOrganizationTypes = filters.organizationTypes ?? [];
        if (isOrganizationTypeSelected(organizationType)) {
            newOrganizationTypes.splice(newOrganizationTypes.indexOf(organizationType), 1);
        } else {
            newOrganizationTypes.push(organizationType);
        }
        await handleChangeFilter('organizationTypes', newOrganizationTypes);
    }

    function isStateSelected(state: string) {
        return !!filters.states?.includes(state);
    }

    async function handleSelectState(state: string) {
        const newStates = filters.states ?? [];
        if (isStateSelected(state)) {
            newStates.splice(newStates.indexOf(state), 1);
        } else {
            newStates.push(state);
        }
        await handleChangeFilter('states', newStates);
    }

    async function handleSort(key: 'name' | 'city') {
        setSortDirection(sortDirection === 'ASC' && sortKey === key ? 'DESC' : 'ASC');
        setSortKey(key);
    }

    async function handleChangeFilter<K extends keyof IOrganizationFilters>(name: K, value: IOrganizationFilters[K]) {
        setFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
        setPage(1);
    }

    async function removeFilter<K extends keyof IOrganizationFilters>(name: K) {
        setFilters((prevFilters) => {
            const { [name]: _, ...newFilters } = prevFilters;

            return newFilters;
        });
        setPage(1);
    }

    async function loadGroupsList() {
        let query = `/api/groups?limit=${groupsFilterListSize}`;
        if (groupsListSearchText) {
            query += `&searchText=${groupsListSearchText}`;
        }
        const result = await abortableFetch.fetchJson(query, 'GET');
        setGroupsList(result.json.items);
    }

    function getDropdownCount(type: OrganizationDropdownFilter) {
        let count = 0;
        switch (type) {
            case OrganizationDropdownFilter.status:
                count += statuses.length;
                break;
            case OrganizationDropdownFilter.kgoStatus:
                count += kgoStatuses.length;
                break;
            case OrganizationDropdownFilter.careGroup:
                count += filters.groupIds?.length ? filters.groupIds.length : 0;
                break;
            case OrganizationDropdownFilter.carePath:
                if (filters.episodeCategoryIds) {
                    count += filters.episodeCategoryIds.length;
                    if (filters.serviceGroupIds) {
                        count += filters.serviceGroupIds.length;
                    }
                }
                break;
            case OrganizationDropdownFilter.organizationType:
                count += filters.organizationTypes?.length ? filters.organizationTypes.length : 0;
                break;
            case OrganizationDropdownFilter.state:
                count += filters.states?.length ? filters.states.length : 0;
                break;
            default:
                never(type);
        }

        return count;
    }

    function formatCityAndZipText(item: IOrganization) {
        let city = item.city ?? '';
        const zip = item.zip?.replace(/\s/g, '') ?? '';
        if (city && zip) {
            city += ', ';
        }

        return `${city}${zip}` || null;
    }

    function resetGroupsListSearchText() {
        setGroupsListSearchText(null);
        setOnGroupsListFilterReset(true);
    }

    function formatCityOrZipSearchTerm(cityOrZip?: string) {
        if (!cityOrZip) {
            return null;
        }
        const isZip = /\d{4}/.test(cityOrZip);

        return isZip ? cityOrZip.replace(/\s/g, '') : cityOrZip;
    }

    function getDropdownFilterTitleAndCount(type: OrganizationDropdownFilter) {
        const count = getDropdownCount(type);
        let translationKey;
        switch (type) {
            case OrganizationDropdownFilter.status:
                translationKey = 'status';
                break;
            case OrganizationDropdownFilter.kgoStatus:
                translationKey = 'kgoStatus';
                break;
            case OrganizationDropdownFilter.careGroup:
                translationKey = 'careGroup';
                break;
            case OrganizationDropdownFilter.carePath:
                translationKey = 'carePaths';
                break;
            case OrganizationDropdownFilter.organizationType:
                translationKey = 'organizationType';
                break;
            case OrganizationDropdownFilter.state:
                translationKey = 'state';
                break;
            default:
                translationKey = '';
                break;
        }

        return `${translate.common[translationKey as keyof typeof translate.common]} ${count ? `(${count})` : ''}`;
    }

    function renderGroupsList(suffix: TextInputSuffix) {
        return (
            <DropdownFilter.AccordionItem
                label={getDropdownFilterTitleAndCount(OrganizationDropdownFilter.careGroup)}
                value={OrganizationDropdownFilter.careGroup}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    pb={4}
                >
                    <TextInput
                        onChange={(e) => setGroupsListSearchText(e.target.value === '' ? null : e.target.value)}
                        onEnterKeyPress={async () => await loadGroupsList()}
                        onReset={() => {
                            resetGroupsListSearchText();
                        }}
                        placeholder={`${translate.groups.groupName}...`}
                        suffix={{ ...suffix, onSuffixClick: () => loadGroupsList() }}
                        value={groupsListSearchText ?? ''}
                    />
                    {groupsList.map((group) => (
                        <Checkbox
                            checked={isGroupSelected(group.id)}
                            key={group.name}
                            label={group.name}
                            onChange={async () => await handleSelectGroup(group.id)}
                        />
                    ))}
                    {groupsFilterListSize <= groupsList.length && (
                        <Link
                            color="minimal"
                            fontWeight="normal"
                            onClick={async () => {
                                setGroupsFilterListSize(groupsFilterListSize + 10);
                                await loadGroupsList();
                            }}
                            variant="button"
                        >
                            Volgende 10 zorggroepen tonen
                        </Link>
                    )}
                </Box>
            </DropdownFilter.AccordionItem>
        );
    }

    function renderOrganizationTypesList() {
        return (
            <DropdownFilter.AccordionItem
                label={getDropdownFilterTitleAndCount(OrganizationDropdownFilter.organizationType)}
                value={OrganizationDropdownFilter.organizationType}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    pb={4}
                >
                    {Object.values(OrganizationType).map((type) => (
                        <Checkbox
                            checked={isOrganizationTypeSelected(type)}
                            key={type}
                            label={translate[`organizationTypeEnum`][type]}
                            onChange={async () => await handleSelectType(type)}
                        />
                    ))}
                </Box>
            </DropdownFilter.AccordionItem>
        );
    }

    function renderStatusChoice() {
        return (
            <DropdownFilter.AccordionItem
                label={getDropdownFilterTitleAndCount(OrganizationDropdownFilter.status)}
                value={OrganizationDropdownFilter.status}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    pb={4}
                >
                    <CheckboxGroup
                        onChange={(choice) => setStatuses(choice as ActiveOrInactive[])}
                        value={statuses}
                    >
                        <Checkbox
                            label={translate.common.isActive}
                            value={ActiveOrInactive.active}
                        />
                        <Checkbox
                            label={translate.common.archived}
                            value={ActiveOrInactive.inactive}
                        />
                    </CheckboxGroup>
                </Box>
            </DropdownFilter.AccordionItem>
        );
    }

    function renderKgoStatusChoice() {
        return (
            <DropdownFilter.AccordionItem
                label={getDropdownFilterTitleAndCount(OrganizationDropdownFilter.kgoStatus)}
                value={OrganizationDropdownFilter.kgoStatus}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    pb={4}
                >
                    <CheckboxGroup
                        onChange={(choice) => setKgoStatuses(choice as OrganizationKgoStatus[])}
                        value={kgoStatuses}
                    >
                        {Object.values(OrganizationKgoStatus).map((status) => (
                            <Checkbox
                                key={status}
                                label={translate.organization.kgoStatuses[status]}
                                value={OrganizationKgoStatus[status]}
                            />
                        ))}
                    </CheckboxGroup>
                </Box>
            </DropdownFilter.AccordionItem>
        );
    }

    function renderStateChoice() {
        return (
            <DropdownFilter.AccordionItem
                label={getDropdownFilterTitleAndCount(OrganizationDropdownFilter.state)}
                value={OrganizationDropdownFilter.state}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    pb={4}
                >
                    {Object.entries(State).map(([stateCode, stateName]) => (
                        <Checkbox
                            checked={isStateSelected(stateName)}
                            key={stateCode}
                            label={stateName}
                            onChange={async () => await handleSelectState(stateName)}
                        />
                    ))}
                </Box>
            </DropdownFilter.AccordionItem>
        );
    }

    function renderCarepathList() {
        return (
            <DropdownFilter.AccordionItem
                label={getDropdownFilterTitleAndCount(OrganizationDropdownFilter.carePath)}
                value={OrganizationDropdownFilter.carePath}
            >
                {serviceGroupMembershipsGroupedByEpisodeCategories.map(
                    (serviceGroupMembershipsGroup: IServicesByServiceGroupForEpisodeCategory) => {
                        const episodeCategory = serviceGroupMembershipsGroup.episodeCategory;
                        const serviceGroups = serviceGroupMembershipsGroup.serviceGroups;

                        return (
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={2}
                                key={serviceGroupMembershipsGroup.episodeCategory.id}
                                pb={2}
                            >
                                <Checkbox
                                    checked={filters.episodeCategoryIds?.includes(episodeCategory.id) ?? false}
                                    key={episodeCategory.name}
                                    label={translate[`episodeCategoryEnum`][episodeCategory.name]}
                                    onChange={async (e) => {
                                        const updatedEpisodeCategoryIds = [...(filters.episodeCategoryIds ?? [])];
                                        let updatedServiceGroupIds = [...(filters.serviceGroupIds ?? [])];
                                        if (e.target.checked) {
                                            updatedEpisodeCategoryIds.push(episodeCategory.id);
                                        } else {
                                            updatedEpisodeCategoryIds.splice(
                                                updatedEpisodeCategoryIds.indexOf(episodeCategory.id),
                                                1
                                            );
                                            updatedServiceGroupIds = updatedServiceGroupIds.filter(
                                                (serviceGroupId) =>
                                                    !serviceGroups
                                                        .map((serviceGroup) => serviceGroup.id)
                                                        .includes(serviceGroupId)
                                            );
                                        }
                                        await handleChangeFilter('serviceGroupIds', updatedServiceGroupIds);
                                        await handleChangeFilter('episodeCategoryIds', updatedEpisodeCategoryIds);
                                    }}
                                />
                                {serviceGroupMembershipsGroup.serviceGroups.length > 1 && (
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={2}
                                        pl={8}
                                    >
                                        {serviceGroups.map((serviceGroup: IServiceGroup) => {
                                            return (
                                                <Checkbox
                                                    checked={
                                                        filters.serviceGroupIds?.includes(serviceGroup.id) ?? false
                                                    }
                                                    key={serviceGroup.id}
                                                    label={serviceGroup.name}
                                                    onChange={async (e) => {
                                                        const updatedServiceGroupIds = [
                                                            ...(filters.serviceGroupIds ?? []),
                                                        ];
                                                        const updatedEpisodeCategoryIds = [
                                                            ...(filters.episodeCategoryIds ?? []),
                                                        ];
                                                        if (e.target.checked) {
                                                            updatedServiceGroupIds.push(serviceGroup.id);
                                                            if (
                                                                !filters.episodeCategoryIds?.includes(
                                                                    episodeCategory.id
                                                                )
                                                            ) {
                                                                updatedEpisodeCategoryIds.push(episodeCategory.id);
                                                            }
                                                        } else {
                                                            const index = updatedServiceGroupIds.indexOf(
                                                                serviceGroup.id
                                                            );
                                                            updatedServiceGroupIds.splice(index, 1);
                                                        }
                                                        await handleChangeFilter(
                                                            'episodeCategoryIds',
                                                            updatedEpisodeCategoryIds
                                                        );
                                                        await handleChangeFilter(
                                                            'serviceGroupIds',
                                                            updatedServiceGroupIds
                                                        );
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            </Box>
                        );
                    }
                )}
            </DropdownFilter.AccordionItem>
        );
    }

    function clearDropDownFilters() {
        setFilters({
            ...filters,
            episodeCategoryIds: undefined,
            groupIds: undefined,
            isActive: undefined,
            organizationTypes: undefined,
            serviceGroupIds: undefined,
            states: undefined,
        });
        setGroupsFilterListSize(10);
        setGroupsListSearchText('');
        setStatuses([]);
        setKgoStatuses([]);

        setOnDropdownFilterReset(true);
    }

    function clearFilters() {
        setFilters({});
        setStatuses([]);
        setKgoStatuses([]);
        setOnDropdownFilterReset(true);
    }

    function getNumberOfAppliedDropdownFilters() {
        let count = 0;
        Object.keys(OrganizationDropdownFilter).forEach(
            (filter) => (count += getDropdownCount(filter as OrganizationDropdownFilter))
        );

        return count;
    }

    async function loadAvailableCarePathsWithServiceGroups() {
        const resultMemberships = await abortableFetch.fetchJson(
            '/api/serviceGroupMemberships?groupByEpisodeCategory=true',
            'GET'
        );
        const resultEpisodeCategories = await abortableFetch.fetchJson('/api/episodeCategories', 'GET');
        const episodeCategories = resultEpisodeCategories.json.items as IEpisodeCategory[];
        const groupedMemberships = resultMemberships.json.items as IServicesByServiceGroupForEpisodeCategory[];
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const serviceGroupMembershipsGroupedByEpisodeCategories = [
            ...groupedMemberships,
            ...episodeCategories
                .filter(
                    (episodeCategory) =>
                        !groupedMemberships.find(
                            (groupedMembership) => groupedMembership.episodeCategory.id === episodeCategory.id
                        )
                )
                .map((episodeCategory) => {
                    return {
                        episodeCategory,
                        serviceGroups: [],
                    };
                }),
        ];

        setServiceGroupMembershipsGroupedByEpisodeCategories(serviceGroupMembershipsGroupedByEpisodeCategories);
    }

    return (
        <PageTemplate>
            <PageHeader
                action={
                    hasOneAuthorization(ManagerAuthorization.organizationAddEdit) && (
                        <Button
                            as={ReactRouterLink}
                            to="/organizations/add"
                            variant="outline"
                        >
                            {translate.organization.add}
                        </Button>
                    )
                }
                title={translate.organization.organizations}
            />
            <Box
                display="flex"
                flexDirection={['column', null, null, 'row']}
                flexGrow={[null, null, null, 1]}
                gap={2}
                px={0}
                py={6}
            >
                <TextInput
                    onChange={async (e) =>
                        await handleChangeFilter('nameOrId', e.target.value === '' ? undefined : e.target.value)
                    }
                    onEnterKeyPress={loadAllData}
                    onReset={async () => {
                        await handleChangeFilter('nameOrId', undefined);
                        setOnTextBoxFilterReset(true);
                    }}
                    placeholder={`${translate.organization.nameOrTxcsId}...`}
                    suffix={suffixProp}
                    value={filters.nameOrId ?? ''}
                />
                <TextInput
                    onChange={async (e) =>
                        await handleChangeFilter('agbCode', e.target.value === '' ? undefined : e.target.value)
                    }
                    onEnterKeyPress={loadAllData}
                    onReset={async () => {
                        await handleChangeFilter('agbCode', undefined);
                        setOnTextBoxFilterReset(true);
                    }}
                    placeholder={`${translate.organization.agbCode}...`}
                    suffix={suffixProp}
                    value={filters.agbCode ?? ''}
                />
                <TextInput
                    onChange={async (e) =>
                        await handleChangeFilter('cityOrZip', e.target.value === '' ? undefined : e.target.value)
                    }
                    onEnterKeyPress={loadAllData}
                    onReset={async () => {
                        await handleChangeFilter('cityOrZip', undefined);
                        setOnTextBoxFilterReset(true);
                    }}
                    placeholder={`${translate.organization.cityOrZip}...`}
                    suffix={suffixProp}
                    value={filters.cityOrZip ?? ''}
                />
                <TextInput
                    onChange={async (e) =>
                        await handleChangeFilter('relationNumber', e.target.value === '' ? undefined : e.target.value)
                    }
                    onEnterKeyPress={loadAllData}
                    onReset={async () => {
                        await handleChangeFilter('relationNumber', undefined);
                        setOnTextBoxFilterReset(true);
                    }}
                    placeholder={`${translate.organization.relationNumber}...`}
                    suffix={suffixProp}
                    value={filters.relationNumber ?? ''}
                />
                <Box display="flex">
                    <DropdownFilter
                        count={getNumberOfAppliedDropdownFilters()}
                        label="Filters"
                        noPadding
                        onReset={clearDropDownFilters}
                        onSubmit={() => loadData()}
                    >
                        <DropdownFilter.Accordion type="multiple">
                            {renderCarepathList()}
                            {renderStatusChoice()}
                            {renderKgoStatusChoice()}
                            {renderStateChoice()}
                            {renderGroupsList({
                                ...suffixProp,
                                onSuffixClick: () => loadGroupsList(),
                            })}
                            {renderOrganizationTypesList()}
                        </DropdownFilter.Accordion>
                    </DropdownFilter>
                    <Box
                        alignSelf="center"
                        ml={4}
                    >
                        <Text
                            size="sm"
                            whiteSpace="nowrap"
                        >
                            <Link
                                color="subtle"
                                fontWeight="normal"
                                onClick={clearFilters}
                                variant="button"
                            >
                                {translate.common.deleteAll}
                            </Link>
                        </Text>
                    </Box>
                </Box>
            </Box>

            {items.length !== 0 && (
                <>
                    <TableContainer>
                        <Table spacing="medium">
                            <TableHead>
                                <TableRow>
                                    <TableHeaderSelectAll
                                        checked={selectedItems.length > 0 || areAllChecked}
                                        indeterminate={!areAllChecked}
                                        onSelect={async () => {
                                            setSelectedItems([]);
                                            setAreAllChecked(!areAllChecked);
                                        }}
                                    />
                                    <TableHeader>{translate.common.Id}</TableHeader>
                                    <TableHeader>{translate.organization.organizationStructureTypeHeader}</TableHeader>
                                    <TableHeader
                                        isSortable={true}
                                        isSortHeader={sortKey === 'name'}
                                        onClick={async () => await handleSort('name')}
                                        sortDirection={sortKey === 'name' ? sortDirection : undefined}
                                    >
                                        {translate.common.name}
                                    </TableHeader>
                                    <TableHeader>{translate.organization.statutoryName}</TableHeader>
                                    <TableHeader sx={{ minWidth: '120px' }}>{translate.common.status}</TableHeader>
                                    <TableHeader>{translate.organization.agbCode}</TableHeader>
                                    <TableHeader>{translate.organization.relationNumber}</TableHeader>
                                    <TableHeader
                                        isSortable={true}
                                        isSortHeader={sortKey === 'city'}
                                        onClick={async () => await handleSort('city')}
                                        sortDirection={sortKey === 'city' ? sortDirection : undefined}
                                    >
                                        {translate.common.city}, postcode
                                    </TableHeader>
                                    <TableHeader>{translate.organization.organizationType}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item) => {
                                    const organizationURL = `/organizations/${item.id}/view`;

                                    return (
                                        <TableRow
                                            key={item.id}
                                            onAuxClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                window.open(organizationURL, '_blank');
                                            }}
                                            onClick={() => {
                                                setShowSelectedOrganizationDrawer(true);
                                                setSelectedOrganizationId(item.id);
                                            }}
                                        >
                                            <TableCellSelectRow
                                                isSelected={isRowSelected(item.id) || areAllChecked}
                                                onClick={(e) => e.stopPropagation()}
                                                onSelect={async () => await handleSelectRow(item.id)}
                                            />
                                            <TableCell>
                                                <Link
                                                    to={organizationURL}
                                                    variant="link"
                                                >
                                                    <TextWithMatchingTermInBold
                                                        customMatchStyling={{
                                                            color: 'var(--kds-color-foreground-secondary)',
                                                        }}
                                                        searchTerm={filters.nameOrId ?? null}
                                                        text={item.txcsId}
                                                    />
                                                </Link>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip
                                                    content={
                                                        translate.organizationStructureTypeEnum[
                                                            item.organizationStructureType
                                                        ]
                                                    }
                                                >
                                                    {organizationStructureTypeIcon(item.organizationStructureType)}
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Box>
                                                        <TextWithMatchingTermInBold
                                                            searchTerm={filters.nameOrId ?? null}
                                                            text={item.name}
                                                        />
                                                    </Box>
                                                    {item.alias && (
                                                        <Box
                                                            sx={{
                                                                fontSize: 'xs',
                                                            }}
                                                        >
                                                            <TextWithMatchingTermInBold
                                                                searchTerm={filters.nameOrId ?? null}
                                                                text={` (${item.alias})`}
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <TextWithMatchingTermInBold
                                                    searchTerm={filters.nameOrId ?? null}
                                                    text={item.nameStatutory}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    color={item.isActive ? 'green' : 'red'}
                                                    text={
                                                        item.isActive
                                                            ? translate.common.isActive
                                                            : translate.common.archived
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextWithMatchingTermInBold
                                                    searchTerm={filters.agbCode ?? null}
                                                    text={findActiveAgbCode(item.agbCodes ?? [])?.agbCode ?? ''}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextWithMatchingTermInBold
                                                    searchTerm={filters.relationNumber ?? null}
                                                    text={item.relationNumber}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextWithMatchingTermInBold
                                                    searchTerm={formatCityOrZipSearchTerm(filters.cityOrZip)}
                                                    text={formatCityAndZipText(item)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {item.organizationType &&
                                                    translate[`organizationTypeEnum`][item.organizationType]}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Pagination
                        isDisabled={isLoading}
                        onChange={handlePaginationChange}
                        page={page}
                        pageSize={pageSize}
                        pageSizes={pageSizes}
                        totalItems={totalItems}
                    />
                </>
            )}
            {items.length === 0 && !isLoading && (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                >
                    <Figure
                        alt="Not found"
                        centered
                        imageUrl="../../../../../assets/images/not-found-search.svg"
                    />
                    <Heading
                        mb={6}
                        size="medium"
                        textAlign="center"
                    >
                        {translate.common.noSearchResults}
                    </Heading>
                    <ButtonRow justify="center">
                        <Button onClick={clearFilters}>{translate.common.deleteSearchTerms}</Button>
                    </ButtonRow>
                </Box>
            )}
            <ActionBar
                isOpen={selectedItems.length !== 0 || areAllChecked}
                onClose={async () => {
                    setSelectedItems([]);
                    setAreAllChecked(false);
                }}
            >
                <Box
                    alignItems="center"
                    display="flex"
                    gap={3}
                    justifyContent="space-between"
                >
                    <BadgeWithLabel
                        color="inverse"
                        label={translate.common.isSelected}
                        value={areAllChecked ? totalItems.toString() : selectedItems.length.toString()}
                    />
                    <Button
                        onClick={() => setShowExportModal(true)}
                        variant="solid"
                    >
                        {translate.common.export}
                    </Button>
                </Box>
            </ActionBar>
            <KgbExportModal
                areAllChecked={areAllChecked}
                deselectItems={() => {
                    setSelectedItems([]);
                    setAreAllChecked(false);
                }}
                exportType={KgbExportType.organization}
                filters={areAllChecked ? filters : undefined}
                isOpen={showExportModal}
                onClose={() => {
                    setShowExportModal(false);
                }}
                selectedItems={areAllChecked ? undefined : selectedItems}
                title={`${translate.organization.exportOrganizations} (${
                    areAllChecked ? totalItems.toString() : selectedItems.length.toString()
                })`}
            />

            <OrganizationListDrawer
                isOpen={showSelectedOrganizationDrawer}
                onClose={(redirect, url) => {
                    setShowSelectedOrganizationDrawer(false);
                    if (redirect && url) {
                        setShouldRedirect(true);
                        setRedirectTo(url);
                    }
                }}
                onCloseComplete={() => {
                    if (shouldRedirect && redirectTo) {
                        navigate(redirectTo);
                        setShouldRedirect(false);
                    }
                }}
                organizationId={selectedOrganizationId}
            />
        </PageTemplate>
    );
}
